import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { IS_AUTH_MOCK } from "@/services/config";
import { UNAUTHORIZED_EVENT } from "@/services/http";
import {
  authLogin,
  authLogout,
  authMe,
  authRefresh,
  authRegister,
  REFRESH_KEY,
  TOKEN_KEY,
  USER_KEY,
  type RegisterPayload,
} from "@/services/ownerApi";
import type { AuthUser } from "@/types";

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (payload: RegisterPayload) => Promise<AuthUser>;
  logout: () => void;
  updateUser: (patch: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function persist(token: string, refreshToken: string, user: AuthUser) {
  localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearStored() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedRefresh = localStorage.getItem(REFRESH_KEY) ?? "";

    if (!storedToken) {
      setLoading(false);
      return;
    }

    setToken(storedToken);
    setRefreshToken(storedRefresh);

    if (IS_AUTH_MOCK) {
      const raw = localStorage.getItem(USER_KEY);
      if (raw) {
        try {
          setUser(JSON.parse(raw) as AuthUser);
        } catch {
          localStorage.removeItem(USER_KEY);
        }
      }
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        // Revalidate the access token against the server.
        const current = await authMe(storedToken);
        if (cancelled) return;
        setUser(current);
        localStorage.setItem(USER_KEY, JSON.stringify(current));
      } catch {
        // Access token rejected — fall back to the refresh token once.
        if (storedRefresh) {
          try {
            const session = await authRefresh(storedRefresh);
            if (cancelled) return;
            persist(session.token, session.refreshToken, session.user);
            setToken(session.token);
            setRefreshToken(session.refreshToken);
            setUser(session.user);
            return;
          } catch {
            // Refresh also failed — fall through and clear the session.
          }
        }
        if (cancelled) return;
        clearStored();
        setToken(null);
        setRefreshToken("");
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // A rejected AUTHENTICATED request means the stored token is no longer valid
  // — typically after the backend was redeployed with a fresh signing secret.
  // Drop the stale session so the user is asked to sign in again, instead of
  // every console screen silently showing demo data while saves quietly fail.
  useEffect(() => {
    const onUnauthorized = () => {
      clearStored();
      setToken(null);
      setRefreshToken("");
      setUser(null);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const session = await authLogin(email, password);
    persist(session.token, session.refreshToken, session.user);
    setToken(session.token);
    setRefreshToken(session.refreshToken);
    setUser(session.user);
    return session.user;
  }, []);

  const register = useCallback(async (payload: RegisterPayload) => {
    const session = await authRegister(payload);
    persist(session.token, session.refreshToken, session.user);
    setToken(session.token);
    setRefreshToken(session.refreshToken);
    setUser(session.user);
    return session.user;
  }, []);

  const logout = useCallback(() => {
    const currentRefresh = refreshToken;
    clearStored();
    setToken(null);
    setRefreshToken("");
    setUser(null);
    void authLogout(currentRefresh);
  }, [refreshToken]);

  const updateUser = useCallback((patch: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, loading, login, register, logout, updateUser }),
    [user, token, loading, login, register, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}